"use client";

import { useEffect, useState } from "react";
import { useRegionStore, useLocationStore } from "@/store/index";

interface LocationGuardProps {
  children: React.ReactNode;
}

/**
 * LocationGuard ensures users have selected both a country and a province
 * before accessing the site. On first visit it forces the unified modal open.
 * Waits for Zustand persist rehydration before checking state.
 */
export function LocationGuard({ children }: LocationGuardProps) {
  const { hasSelectedRegion, setRegionModalOpen } = useRegionStore();
  const { hasSelectedLocation } = useLocationStore();
  const [isRehydrated, setIsRehydrated] = useState(false);

  useEffect(() => {
    const unsubRegion = useRegionStore.persist.onFinishHydration(() => {
      setIsRehydrated(true);
    });
    const unsubLocation = useLocationStore.persist.onFinishHydration(() => {
      setIsRehydrated(true);
    });

    if (
      useRegionStore.persist.hasHydrated() &&
      useLocationStore.persist.hasHydrated()
    ) {
      setIsRehydrated(true);
    }

    return () => {
      unsubRegion();
      unsubLocation();
    };
  }, []);

  useEffect(() => {
    if (!isRehydrated) return;

    if (!hasSelectedRegion || !hasSelectedLocation) {
      setRegionModalOpen(true);
    }
  }, [isRehydrated, hasSelectedRegion, hasSelectedLocation, setRegionModalOpen]);

  if (!isRehydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="border-green-main mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
